import { Field } from '../../atoms/forms';
import { Product } from '../../atoms/product';
import { fieldTypeToFilterType } from '../../atoms/additionalFieldColumns';
import advancedFiltersInterpretor from '../../components_v2/filter/AdvancedFiltersInterpretor';
import { FilterId, FilterTree } from '../../components_v2/filter/model/Model';
import { Filter, FilterParameter } from '../../components_v2/filter/pages/FilterList';
import { translateToString } from '../../styles/global/translate';
import { AdditionalColumn } from '../../atoms/additionalColumns';
import { AdditionalFieldColumn } from '../../atoms/additionalFieldColumns';
import { CalculatedField } from '../../atoms/calculatedFields';
import { generateFormFilters } from '../client-companies/CompaniesAndMapView';
import { getCompanies, getCompanyFilters } from '../client-companies/data/action';
import { Promotion } from './model';

// Filters over what a promotion is linked to, not over its own fields:
// - linked_company wraps a regular company filter; the matching company ids
//   are resolved by /client-companies/query and the promotion matches when it
//   is linked to at least one of them
// - linked_product wraps a product attribute (hardcoded or custom field),
//   compared locally against the product list
export const LINKED_COMPANY = 'linked_company';
export const LINKED_PRODUCT = 'linked_product';

export type ProductLeafId = 'product_name' | 'product_reference' | 'product_barcode' | 'product_upc' | 'product_price' | 'product_width' | 'product_height' | 'product_depth' | { product_brand: null } | { product_category: null } | { product_field: number } | { product_dim_field: number };

export type LinkedFilterContext = {
	companySets: Record<string, Set<number>>,
	productsById: Map<number, Product>,
	productFieldValues: Map<number, Map<string, unknown>>,
	// product-level ({p, f}) values of the multi-dimension fields
	dimensionValues?: Map<number, Map<string, unknown>>,
}

// The company filter list of the companies page, with plain (unwrapped) ids
export async function companyFilterList(
	additionalColumns: AdditionalColumn[],
	additionalFieldColumns: AdditionalFieldColumn[],
	calculatedFields: CalculatedField[],
): Promise<FilterParameter[]> {
	const response = await getCompanyFilters();
	const generated = await generateFormFilters(additionalColumns, additionalFieldColumns, calculatedFields);
	return [...response.filters, ...generated.filters]
		.map(cat => ({ category: cat.category, filters: cat.filters.filter(f => !f.jsx) }))
		.filter(cat => cat.filters.length > 0);
}

// The product filters (hardcoded attributes + product custom fields), with
// plain (unwrapped) ids
export function productFilterList(fields: Field[], formCollectedFieldIds?: Set<number>): Filter[] {
	const formCollected = (f: Field) => !formCollectedFieldIds || formCollectedFieldIds.has(f.id);
	return [
		{ id: 'product_name', name: translateToString('name'), type: 'string' as const },
		{ id: 'product_reference', name: translateToString('reference'), type: 'string' as const },
		{ id: 'product_barcode', name: translateToString('EAN-JAN barcode'), type: 'string' as const },
		{ id: 'product_upc', name: translateToString('UPC Barcode'), type: 'string' as const },
		{ id: 'product_price', name: translateToString('price'), type: 'numeric' as const },
		{ id: 'product_width', name: `${translateToString('width')} (cm)`, type: 'numeric' as const },
		{ id: 'product_height', name: `${translateToString('height')} (cm)`, type: 'numeric' as const },
		{ id: 'product_depth', name: `${translateToString('depth')} (cm)`, type: 'numeric' as const },
		{ id: { product_brand: null } as unknown as FilterId, name: translateToString('brand'), type: 'select' as const },
		{ id: { product_category: null } as unknown as FilterId, name: translateToString('category'), type: 'select' as const },
		// the form-collected entries only exist for fields some form actually
		// collected values for — a field tagged Product purely for the catalog
		// would get a dead filter that can never match anything
		...fields.filter(f => f.is_product && formCollected(f)).map(f => ({
			id: { product_field: f.id } as unknown as FilterId,
			name: f.name,
			type: fieldTypeToFilterType(f.type),
		})),
		// the same Product-flagged fields, filtering on their product-level
		// value ({p, f} in additional_values); the suffix only disambiguates
		// from a form-collected twin sharing the same field name
		...fields.filter(f => f.is_product).map(f => ({
			id: { product_dim_field: f.id } as unknown as FilterId,
			name: formCollected(f) ? `${f.name} (${translateToString('product.assortments')})` : f.name,
			type: fieldTypeToFilterType(f.type),
		})),
	];
}

export function wrapFilterParams(params: FilterParameter[], wrapper: string, categoryPrefix?: string): FilterParameter[] {
	const wrap = (f: Filter): Filter => ({ ...f, id: { wrapper, [wrapper]: f.id } as unknown as FilterId });
	return params.map(cat => ({
		category: categoryPrefix ? `${categoryPrefix} - ${cat.category}` : cat.category,
		filters: cat.filters.map(wrap),
	}));
}

export function linkedCompanyLeafKey(column: FilterId, operator: string, value: string | undefined): string {
	return JSON.stringify([column, operator, value ?? null]);
}

export function collectLinkedCompanyLeaves(
	tree: FilterTree | undefined,
	acc: { column: FilterId, operator: string, value?: string, filter_type?: unknown }[] = [],
): { column: FilterId, operator: string, value?: string, filter_type?: unknown }[] {
	if (!tree) return acc;
	if ('and' in tree) { tree.and.forEach(t => collectLinkedCompanyLeaves(t, acc)); return acc; }
	if ('or' in tree) { tree.or.forEach(t => collectLinkedCompanyLeaves(t, acc)); return acc; }
	const column = tree.val.column;
	if (typeof column !== 'string' && 'wrapper' in column && column.wrapper === LINKED_COMPANY) acc.push(tree.val);
	return acc;
}

// Resolves a plain company filter tree into the matching companies.
// The name column must be asked for: an empty column list returns ids only
export async function resolveCompanyTree(tree: FilterTree): Promise<{ id: number, name: string }[]> {
	const res = await getCompanies({ new_filters: tree, columns: ['name'], order_bys: undefined }, true);
	return res.companies.map(c => ({ id: c.company_id, name: c.name }));
}

// The dimension fields a filter tree references (wrapped or not): only their
// values need fetching — a page must not pull every field's values up front
export function collectProductDimFieldIds(tree: FilterTree | undefined, acc: Set<number> = new Set()): Set<number> {
	if (!tree) return acc;
	if ('and' in tree) { tree.and.forEach(t => collectProductDimFieldIds(t, acc)); return acc; }
	if ('or' in tree) { tree.or.forEach(t => collectProductDimFieldIds(t, acc)); return acc; }
	let column: unknown = tree.val.column;
	while (column && typeof column === 'object' && 'wrapper' in column) column = (column as Record<string, unknown>)[(column as { wrapper: string }).wrapper];
	if (column && typeof column === 'object' && 'product_dim_field' in column) acc.add((column as { product_dim_field: number }).product_dim_field);
	return acc;
}

// What moment's toISOString() writes, which is what every date filter sends
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export function compareLeafValue(fieldValue: unknown, op: string, compare: string | undefined): boolean {
	const isEmpty = fieldValue === undefined || fieldValue === null || fieldValue === ''
		|| (Array.isArray(fieldValue) && fieldValue.length === 0);
	if (op === 'empty') return isEmpty;
	if (op === 'not_empty') return !isEmpty;
	if (fieldValue === undefined || fieldValue === null) return false;
	// A Multiselect value is an array: operators test each element on its own
	// and one matching element is enough (OR), never the joined whole
	const texts = (Array.isArray(fieldValue) ? fieldValue : [fieldValue]).map(v => String(v).toLowerCase());
	const text = texts.join(',');
	const compareText = (compare ?? '').toLowerCase();
	// Multi-choice filters arrive comma-joined and matching any choice is
	// enough (OR); whole-text equality stays first so a free-typed value
	// containing a comma still compares exactly
	const compareList = compareText.split(',');
	// A Date field holds an ISO timestamp and the date filters send ISO
	// timestamps too. Read by parseFloat, '2026-10-01T…' is 2026, so every
	// date of the same year compared equal: gt and lt matched nothing while
	// between matched every product. The literal shape is what tells a
	// timestamp from a number — '10' is never a date
	const number = (value: unknown): number => {
		if (typeof value === 'string' && ISO_TIMESTAMP.test(value)) return Date.parse(value);
		return typeof value === 'number' ? value : parseFloat(String(value));
	};
	const numeric = number(fieldValue);
	switch (op) {
		case 'eq': return text === compareText || texts.some(t => compareList.includes(t));
		case 'ne': return text !== compareText && !texts.some(t => compareList.includes(t));
		case 'gt': case 'gte': case 'lt': case 'lte': {
			const n = number(compare);
			if (isNaN(n) || isNaN(numeric)) return false;
			return op === 'gt' ? numeric > n : op === 'gte' ? numeric >= n : op === 'lt' ? numeric < n : numeric <= n;
		}
		case 'contains': return texts.some(t => t.includes(compareText));
		case 'not_contains': return !texts.some(t => t.includes(compareText));
		case 'starts_with': return texts.some(t => t.startsWith(compareText));
		case 'ends_with': return texts.some(t => t.endsWith(compareText));
		case 'contained': return compareList.some(v => texts.includes(v));
		case 'not_contained': return !compareList.some(v => texts.includes(v));
		case 'between': case 'not_between': {
			// an ISO timestamp carries no comma, so the split holds for dates
			const [l, r] = (compare ?? '').split(',').map(number);
			if ([l, r, numeric].some(isNaN)) return false;
			const inside = l <= numeric && numeric <= r;
			return op === 'between' ? inside : !inside;
		}
	}
	return false;
}

export function productLeafValue(product: Product | undefined, leaf: ProductLeafId, productFieldValues: Map<number, Map<string, unknown>>, dimensionValues?: Map<number, Map<string, unknown>>): unknown {
	if (typeof leaf === 'object' && 'product_field' in leaf) {
		const uuid = product?.uuid;
		return uuid ? productFieldValues.get(leaf.product_field)?.get(uuid) : undefined;
	}
	if (typeof leaf === 'object' && 'product_dim_field' in leaf) {
		const uuid = product?.uuid;
		return uuid ? dimensionValues?.get(leaf.product_dim_field)?.get(uuid) : undefined;
	}
	if (typeof leaf === 'object' && 'product_brand' in leaf) return product?.brand;
	if (typeof leaf === 'object' && 'product_category' in leaf) return product?.category;
	switch (leaf) {
		case 'product_name': return product?.name;
		case 'product_reference': return product?.reference;
		case 'product_barcode': return product?.barcode;
		case 'product_upc': return product?.code_upc;
		case 'product_price': return product?.price;
		case 'product_width': return product?.width;
		case 'product_height': return product?.height;
		case 'product_depth': return product?.depth;
	}
	return undefined;
}

// Evaluates a plain (unwrapped) product filter tree against one product
export function matchProductTree(product: Product, tree: FilterTree | undefined, productFieldValues: Map<number, Map<string, unknown>>, dimensionValues?: Map<number, Map<string, unknown>>): boolean {
	const ops = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'contains', 'not_contains', 'contained', 'not_contained',
		'between', 'not_between', 'empty', 'not_empty', 'starts_with', 'ends_with'];
	const options = Object.fromEntries(ops.map(op => [op, (value: Product, a?: unknown, b?: unknown) => {
		const noValue = op === 'empty' || op === 'not_empty';
		const leaf = (noValue ? a : b) as ProductLeafId;
		const compare = noValue ? undefined : a as string | undefined;
		return compareLeafValue(productLeafValue(value, leaf, productFieldValues, dimensionValues), op, compare);
	}]));
	return advancedFiltersInterpretor(product, tree, options);
}

export function linkedMatch(promotion: Promotion, filterId: FilterId, op: string, compare: string | undefined, ctx: LinkedFilterContext): boolean | undefined {
	if (typeof filterId === 'string' || !('wrapper' in filterId)) return undefined;
	if (filterId.wrapper === LINKED_COMPANY) {
		// the operator already ran server-side: membership is the whole test
		const set = ctx.companySets[linkedCompanyLeafKey(filterId, op, compare)];
		return set ? promotion.companies.some(c => set.has(c.id)) : false;
	}
	if (filterId.wrapper === LINKED_PRODUCT) {
		const leaf = filterId[LINKED_PRODUCT] as unknown as ProductLeafId;
		return promotion.products.some(p => compareLeafValue(productLeafValue(ctx.productsById.get(p.id), leaf, ctx.productFieldValues, ctx.dimensionValues), op, compare));
	}
	return undefined;
}
